import * as React from "react";

import { ComicPanel } from "@/components/comic";
import { CalendarSlot } from "@/features/booking/BookingCalendar";
import { formatSlotRange } from "@/lib/datetime";

/**
 * The hour a visitor pointed at, shown back to them before anything is asked of anyone.
 *
 * <p>A slot chip used to be the booking: one click on a calendar and the request had gone, an hour
 * was held, and a stranger's diary had a meeting in it. Pointing at a time on a grid is not the
 * same act as asking for it, and the interface should not treat the two as one. So the pick lands
 * here first, in the visitor's own words back to them, and only the button below sends anything.</p>
 *
 * <p>Leaving is as cheap as arriving. Escape, or a click anywhere off the card, puts the calendar
 * back with nothing sent and nothing said to the host, because a review step that is hard to back
 * out of is just the old one-click booking with an extra press in it.</p>
 */
export function GuestSlotConfirmCard({
  slot,
  handle,
  busy = false,
  onConfirm,
  onCancel,
}: {
  slot: CalendarSlot;
  /** The host being asked, without the leading @. */
  handle: string;
  /** A confirmation already in flight: the card stops being dismissable so the answer is not lost. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const card = React.useRef<HTMLDivElement | null>(null);
  const confirm = React.useRef<HTMLButtonElement | null>(null);

  // The keyboard follows the pick, so Escape and Enter both land on the decision that was just made.
  React.useEffect(() => {
    confirm.current?.focus();
  }, []);

  React.useEffect(() => {
    if (busy) return undefined;
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    const away = (e: Event) => {
      const target = e.target as Node | null;
      if (target && card.current && !card.current.contains(target)) onCancel();
    };
    document.addEventListener("keydown", key);
    // pointerdown, not click: a press that starts outside the card is already a decision to leave,
    // and waiting for the release lets the press land on whatever was underneath.
    document.addEventListener("pointerdown", away);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", away);
    };
  }, [busy, onCancel]);

  return (
    <div ref={card}>
      <ComicPanel title="Before I ask">
        <p className="text-sm font-medium">{formatSlotRange(slot.start, slot.end)}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{`with @${handle}`}</p>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            ref={confirm}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-xl bg-brand-blue px-3 py-2 text-sm font-medium text-white transition hover:-translate-y-px disabled:opacity-60"
          >
            Ask for this time
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:-translate-y-px disabled:opacity-60"
          >
            Pick another
          </button>
        </div>
      </ComicPanel>
    </div>
  );
}
